import type { StayChip, StayFull, StaySource } from '../../../../interfaces/stays';
import type { PillTone } from '../../dashboard/dashboard-stays-components/stays-labels';
import { plural } from '../../../../function-utils/plural';
import { formatMoney } from '../../../../utils/stays-money';

/**
 * LOBBERO STAYS — the stays table's words (plan S4, R-S4.25/R-S4.26), as pure
 * functions so the table, the Overview's "Next arrivals" and the calendar's
 * reuse of the row all say the same thing.
 */

type Lead = Pick<StayFull, 'lead'>;

/** "Ana Silva" — or, for a stay a booking site's calendar brought with no name (plan S5), "Airbnb guest". */
export const fullName = (s: Lead & { source?: StaySource }): string =>
  [s.lead.first_name, s.lead.last_name].filter(Boolean).join(' ') ||
  (s.source && s.source !== 'direct' && s.source !== 'other' ? `${SOURCE_LABEL[s.source]} guest` : 'Guest');

/** "Ana" — the confirmations' word for the guest. */
export const firstOf = (s: Lead): string => s.lead.first_name || 'the guest';

/** "Ana S." — first name and last initial (the Overview and the turnaround line). */
export const shortName = (s: Lead): string => {
  const initial = (s.lead.last_name || '').trim().charAt(0);

  return initial ? `${s.lead.first_name} ${initial.toUpperCase()}.` : s.lead.first_name || 'Guest';
};

/** "AS" for the avatar (decorative). */
export const initialsOf = (s: Lead): string =>
  `${(s.lead.first_name || '').trim().charAt(0)}${(s.lead.last_name || '').trim().charAt(0)}`.toUpperCase() || '·';

export const SOURCE_LABEL: Record<StaySource, string> = {
  direct: 'Direct',
  airbnb: 'Airbnb',
  booking: 'Booking.com',
  vrbo: 'Vrbo',
  other: 'Other',
};

export const sourceText = (source: StaySource): string => SOURCE_LABEL[source] || 'Other';

/** "3 nights" */
export const nightsLabel = (n: number): string => `${n} ${plural(n, 'night')}`;

/** "2 guests" */
export const guestsLabel = (n: number): string => `${n} ${plural(n, 'guest')}`;

/** The cleaning pill — read-only on the table (the edit modal changes it). */
export const cleaningPill = (s: Pick<StayFull, 'cleaning'>): { tone: PillTone; text: string } => {
  const c = s.cleaning;
  if (!c || c.status === 'unscheduled') return { tone: 'neutral', text: 'Cleaning not scheduled' };
  if (c.status === 'completed') return { tone: 'success', text: 'Cleaned' };
  if (c.type === 'agency') return { tone: 'warning', text: 'Agency cleaning scheduled' };
  if (c.type === 'self') return { tone: 'warning', text: 'Self cleaning scheduled' };

  return { tone: 'warning', text: 'Cleaning scheduled' };
};

/** The timing chip's tone (its words are stays-stage timingText). */
export const timingTone = (s: Pick<StayFull, 'stage' | 'archived_at'>): PillTone => {
  if (s.archived_at) return 'neutral';
  switch (s.stage.key) {
    case 'in_house':
    case 'departure_day':
      return 'success';
    case 'arrival_day':
      return 'brand';
    default:
      return 'neutral';
  }
};

export const isInHouse = (s: Pick<StayFull, 'stage'>): boolean =>
  s.stage.key === 'in_house' || s.stage.key === 'departure_day';

export const isUpcoming = (s: Pick<StayFull, 'stage' | 'status' | 'archived_at'>): boolean =>
  !s.archived_at && s.status === 'confirmed' && (s.stage.key === 'upcoming' || s.stage.key === 'arrival_day');

export const isNoShow = (s: Pick<StayFull, 'status' | 'stage'>): boolean =>
  s.status === 'no_show' || s.stage.key === 'no_show';

/** The gross in the stay's own currency, or '' when none is recorded. */
export const grossText = (s: Pick<StayFull, 'money'>): string =>
  s.money && s.money.gross_cents != null ? formatMoney(s.money.gross_cents, s.money.currency) : '';

/** "Net €300.00", or null without a payout. */
export const netText = (s: Pick<StayFull, 'money'>): string | null =>
  s.money && s.money.payout_cents != null ? `Net ${formatMoney(s.money.payout_cents, s.money.currency)}` : null;

/** The stage chips, in the order the table shows them. */
export const CHIPS: ReadonlyArray<{ key: StayChip; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'in_house', label: 'In house' },
  { key: 'departed', label: 'Departed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'no_show', label: 'No-show' },
];

/** "Upcoming (3)", or the bare label before the counts arrive. */
export const chipText = (label: string, count: number | undefined): string =>
  typeof count === 'number' ? `${label} (${count})` : label;
